import type { FilterDefinition } from '@fashionnovateam/nova-ui-toolkit';
import type { SampleRequestFilterValues, VendorContact } from './types';

function getContactName(contact: VendorContact): string {
  return `${contact.first_name} ${contact.last_name || ''}`.trim();
}

export const QUOTE_STATUS_MAP: Record<string, string[]> = {
  'Quote Not Sent': ['RequestSentToVendor'],
  'Quote Sent': ['Received', 'TargetPriceAdded', 'VendorApprovalFailed'],
  'Quote Approved': ['Approved', 'VendorApprovalCompleted'],
  'Quote Rejected': ['Rejected'],
};

export const PREV_SAMPLE_STATUS_MAP: Record<string, string[]> = {
  Pending: [
    'Pending',
    'Ordered',
    'Received',
    'NotAvailable',
    'samples_requested_to_library',
    'samples_requested_to_dc',
    'samples_ready_to_pickup_in_library',
    'samples_in_transit_from_dc_to_hq',
  ],
  'Shipped to You': ['ShippedToVendor'],
  'Received by You': ['ReceivedByVendor'],
};

export const VENDOR_SAMPLE_STATUS_MAP: Record<string, string[]> = {
  Pending: ['Pending'],
  'Shipped to FN': ['Shipped'],
  'Received by FN': ['Received'],
};

export type CheckboxFilterConfig = {
  property: keyof SampleRequestFilterValues;
  backendMap?: Record<string, string[]>;
};

export const CHECKBOX_FILTERS: CheckboxFilterConfig[] = [
  { property: 'quoteStatus', backendMap: QUOTE_STATUS_MAP },
  { property: 'prevSampleStatus', backendMap: PREV_SAMPLE_STATUS_MAP },
  { property: 'counterSampleStatus', backendMap: VENDOR_SAMPLE_STATUS_MAP },
  { property: 'priorities' },
  { property: 'vendorContacts' },
];

export const CHECKBOX_PROPERTIES = new Set(
  CHECKBOX_FILTERS.map((f) => f.property),
);

const NUMBER_CONDITIONS = [
  { label: 'equals', value: 'eq' },
  { label: 'greater than or equal', value: 'gte' },
  { label: 'less than or equal', value: 'lte' },
  { label: 'between', value: 'between', isRange: true },
  { label: 'is empty', value: 'is_empty', isEmpty: true },
  { label: 'is not empty', value: 'is_not_empty', isEmpty: true },
];

const TEXT_CONDITIONS = [
  { label: 'contains', value: 'contains' },
  { label: 'does not contain', value: 'not_contains' },
  { label: 'is empty', value: 'is_empty', isEmpty: true },
  { label: 'is not empty', value: 'is_not_empty', isEmpty: true },
];

const DATE_CONDITIONS = [
  { label: 'before', value: 'before' },
  { label: 'after', value: 'after' },
  { label: 'between', value: 'between', isRange: true },
  { label: 'is empty', value: 'is_empty', isEmpty: true },
  { label: 'is not empty', value: 'is_not_empty', isEmpty: true },
];

export function buildFilterDefinitions(
  contacts: VendorContact[],
): FilterDefinition[] {
  return [
    {
      id: 'quoteStatus',
      label: 'Quote Status',
      type: 'checkbox',
      options: Object.keys(QUOTE_STATUS_MAP).map((l) => ({
        label: l,
        value: l,
      })),
    },
    {
      id: 'prevSampleStatus',
      label: 'Prev. Sample Status',
      type: 'checkbox',
      options: Object.keys(PREV_SAMPLE_STATUS_MAP).map((l) => ({
        label: l,
        value: l,
      })),
    },
    {
      id: 'counterSampleStatus',
      label: 'Vendor Sample Status',
      type: 'checkbox',
      options: Object.keys(VENDOR_SAMPLE_STATUS_MAP).map((l) => ({
        label: l,
        value: l,
      })),
    },
    {
      id: 'vendorContacts',
      label: 'Contacts',
      type: 'checkbox',
      options: contacts.map((c) => ({
        label: getContactName(c),
        value: c.id,
      })),
    },
    {
      id: 'priorities',
      label: 'Priority',
      type: 'checkbox',
      options: [
        { label: 'Immediate', value: 'Immediate' },
        { label: 'Pre-Season Q3Q4', value: 'PreseasonQ3Q4' },
        { label: 'Reorders', value: 'Reorders' },
        { label: 'Pre-Season Q1Q2', value: 'PreseasonQ1Q2' },
      ],
    },
    {
      id: 'fabricCodeName',
      label: 'Fabric Code Name',
      type: 'text',
      conditions: TEXT_CONDITIONS,
    },
    {
      id: 'fabricType',
      label: 'Fabric Type',
      type: 'text',
      conditions: TEXT_CONDITIONS,
    },
    {
      id: 'fabricContent',
      label: 'Fabric Content',
      type: 'text',
      conditions: TEXT_CONDITIONS,
    },
    {
      id: 'productDescription',
      label: 'Product Details',
      type: 'text',
      conditions: TEXT_CONDITIONS,
    },
    {
      id: 'jrCost',
      label: 'JR Cost',
      type: 'currency',
      conditions: NUMBER_CONDITIONS,
      currency: 'USD',
    },
    {
      id: 'plusCost',
      label: 'Plus Cost',
      type: 'currency',
      conditions: NUMBER_CONDITIONS,
      currency: 'USD',
    },
    { id: 'moq', label: 'MOQ', type: 'number', conditions: NUMBER_CONDITIONS },
    { id: 'mcq', label: 'MCQ', type: 'number', conditions: NUMBER_CONDITIONS },
    {
      id: 'leadTime',
      label: 'Lead Time',
      type: 'number',
      conditions: NUMBER_CONDITIONS,
    },
    {
      id: 'reorderLeadTime',
      label: 'Reorder Lead Time',
      type: 'number',
      conditions: NUMBER_CONDITIONS,
    },
    {
      id: 'requestedOn',
      label: 'Requested On',
      type: 'date',
      conditions: DATE_CONDITIONS,
    },
  ];
}
